import React from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@eluve/components';

export interface AffectedTenant {
  tenantId: string;
  name: string;
  otherBundleCount: number;
}

export interface AffectedUser {
  userId: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  tenantName?: string;
}

export interface ReplacementBundleOption {
  id: string;
  displayName: string;
}

const NO_REPLACEMENT = 'NONE';

interface DisableBundleConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'global' | 'tenant';
  bundleName: string;
  affectedTenants: AffectedTenant[];
  affectedUsers: AffectedUser[];
  replacementOptions: ReplacementBundleOption[];
  replacementBundleId: string | null;
  onReplacementBundleChange: (bundleId: string | null) => void;
  pendingAppointmentCount: number;
  isLoading: boolean;
  isDisabling: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DisableBundleConfirmationDialog: React.FC<
  DisableBundleConfirmationDialogProps
> = ({
  isOpen,
  onOpenChange,
  mode,
  bundleName,
  affectedTenants,
  affectedUsers,
  replacementOptions,
  replacementBundleId,
  onReplacementBundleChange,
  pendingAppointmentCount,
  isLoading,
  isDisabling,
  onConfirm,
  onCancel,
}) => {
  const hasAffectedEntities =
    affectedTenants.length > 0 || affectedUsers.length > 0;
  const replacementName =
    replacementOptions.find((o) => o.id === replacementBundleId)?.displayName ??
    null;

  const orphanedTenantCount = affectedTenants.filter(
    (t) => t.otherBundleCount === 0,
  ).length;
  const hasOrphanedTenants = orphanedTenantCount > 0;
  const noReplacementsAvailable =
    hasOrphanedTenants && replacementOptions.length === 0;
  const mustPickReplacement = hasOrphanedTenants && !noReplacementsAvailable;
  const isConfirmBlocked =
    noReplacementsAvailable || (mustPickReplacement && !replacementBundleId);

  const staticDescription =
    mode === 'global'
      ? 'This will disable the bundle for all tenants. Review the impact below.'
      : 'This will disable the bundle for this organization. Review the impact below.';

  const pickerHint = replacementName
    ? `Affected tenants and user defaults will be migrated to "${replacementName}".`
    : 'Leave blank to disable without migrating — affected user defaults will be cleared.';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent size="3xl" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-text-secondary">
            Disable {bundleName}?
          </DialogTitle>
          <DialogDescription>{staticDescription}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Stack
            className="py-6"
            direction="horizontal"
            justify="center"
            align="stretch"
          >
            <LoadingIndicator />
          </Stack>
        ) : (
          <Stack gap={4}>
            {!hasAffectedEntities && (
              <Text size="xs" color="tertiary">
                No tenants or users will be affected.
              </Text>
            )}

            {mode === 'global' && affectedTenants.length > 0 && (
              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  Affected Tenants ({affectedTenants.length})
                </Text>
                <Text size="xs" color="tertiary">
                  {replacementName
                    ? `The listed organizations will be migrated to "${replacementName}".`
                    : 'The bundle will be disabled for the listed organizations. Those with 0 other enabled bundles will be left without an active bundle.'}
                </Text>
                {hasOrphanedTenants && (
                  <Alert
                    variant={
                      noReplacementsAvailable ? 'destructive' : 'warning'
                    }
                  >
                    <Icon name="AlertTriangle" />
                    <AlertTitle>
                      {orphanedTenantCount === 1
                        ? '1 tenant will have no enabled bundles'
                        : `${orphanedTenantCount} tenants will have no enabled bundles`}
                    </AlertTitle>
                    <AlertDescription>
                      {noReplacementsAvailable
                        ? 'No replacement bundles are available. Enable another bundle before disabling this one.'
                        : 'Pick a replacement bundle below to migrate them, or cancel and enable another bundle for these tenants first.'}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="max-h-48 overflow-y-auto rounded-md border border-border-secondary">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organization</TableHead>
                        <TableHead>Other Enabled Bundles</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affectedTenants.map((tenant) => {
                        const isOrphan = tenant.otherBundleCount === 0;
                        return (
                          <TableRow
                            key={tenant.tenantId}
                            className={
                              isOrphan ? 'bg-bg-warning-subtle' : undefined
                            }
                          >
                            <TableCell>{tenant.name}</TableCell>
                            <TableCell>{tenant.otherBundleCount}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </Stack>
            )}

            {affectedUsers.length > 0 && (
              <Stack gap={2}>
                <Text size="xs" weight="semibold">
                  Affected Users ({affectedUsers.length})
                </Text>
                <Text size="xs" color="tertiary">
                  {replacementName
                    ? `The default template bundle will be updated to "${replacementName}" for all listed users.`
                    : 'The default template bundle will be cleared for all listed users.'}
                </Text>
                <div className="max-h-48 overflow-y-auto rounded-md border border-border-secondary">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        {mode === 'global' && (
                          <TableHead>Organization</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affectedUsers.map((user) => (
                        <TableRow key={`${user.userId}-${user.tenantId}`}>
                          <TableCell>
                            {user.firstName} {user.lastName}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          {mode === 'global' && (
                            <TableCell>{user.tenantName}</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Stack>
            )}

            {replacementName && pendingAppointmentCount > 0 && (
              <Text size="xs" color="tertiary">
                {pendingAppointmentCount === 1
                  ? `1 pending appointment will be repointed to "${replacementName}".`
                  : `${pendingAppointmentCount} pending appointments will be repointed to "${replacementName}".`}
              </Text>
            )}

            {replacementOptions.length > 0 && (
              <Stack gap={1}>
                <Label htmlFor="replacement-bundle">
                  Replace with (optional)
                </Label>
                <Select
                  value={replacementBundleId ?? NO_REPLACEMENT}
                  onValueChange={(value) =>
                    onReplacementBundleChange(
                      value === NO_REPLACEMENT ? null : value,
                    )
                  }
                  disabled={isDisabling}
                >
                  <SelectTrigger id="replacement-bundle" className="w-full">
                    <SelectValue placeholder="None — just disable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_REPLACEMENT}>
                      None — just disable
                    </SelectItem>
                    {replacementOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Text size="xs" color="tertiary">
                  {pickerHint}
                </Text>
              </Stack>
            )}
          </Stack>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading || isDisabling || isConfirmBlocked}
          >
            {isDisabling ? 'Disabling...' : 'Disable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
